import { useState, useCallback } from 'react';
import { apiClient } from '../lib/apiClient';
import { Channel } from '../types/channel';

interface UseChannelShareResult {
  copying: boolean;
  regenerating: boolean;
  showPrivateNotice: boolean;
  dismissPrivateNotice: () => void;
  copyShareLink: (channel: Channel) => Promise<void>;
  regenerateShareLink: (channel: Channel) => Promise<Channel | null>;
}

export const useChannelShare = (
  onChannelUpdated?: (channel: Channel) => void
): UseChannelShareResult => {
  const [copying, setCopying] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  // Shown after copying a private channel link so the owner knows access is restricted.
  const [showPrivateNotice, setShowPrivateNotice] = useState(false);

  const dismissPrivateNotice = useCallback(() => setShowPrivateNotice(false), []);

  const copyShareLink = useCallback(async (channel: Channel) => {
    setCopying(true);
    try {
      // Build a frontend URL so recipients open AIRecording directly, not raw JSON from the API.
      const url = channel.shareToken
        ? `${window.location.origin}/?channelToken=${encodeURIComponent(channel.shareToken)}&slug=${encodeURIComponent(channel.slug || '')}`
        : `${window.location.origin}/?channel=${encodeURIComponent(channel.handle)}`;
      await navigator.clipboard.writeText(url);
    } catch {
      const el = document.createElement('textarea');
      el.value = channel.shareToken
        ? `${window.location.origin}/?channelToken=${encodeURIComponent(channel.shareToken)}&slug=${encodeURIComponent(channel.slug || '')}`
        : `${window.location.origin}/?channel=${encodeURIComponent(channel.handle)}`;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    } finally {
      setTimeout(() => setCopying(false), 1500);
      // Show the privacy notice if the channel is private.
      if (channel.visibility === 'private') {
        setShowPrivateNotice(true);
      }
    }
  }, []);

  const regenerateShareLink = useCallback(async (channel: Channel): Promise<Channel | null> => {
    setRegenerating(true);
    try {
      const result = await apiClient.regenerateChannelShareLink(channel.id);
      if (result.data) {
        onChannelUpdated?.(result.data);
        return result.data;
      }
      return null;
    } finally {
      setRegenerating(false);
    }
  }, [onChannelUpdated]);

  return { copying, regenerating, showPrivateNotice, dismissPrivateNotice, copyShareLink, regenerateShareLink };
};
